import { useCallback, useEffect, useState } from 'react';
import { getSelectedAccountInfo } from '@ibk/shared-auth';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { useTranslation } from '@ibk/shared-translation';
import { FeatureFlagUseCase } from '@ibk/shared-types';
import { parseCurrencyToNumber } from '@ibk/shared-utils';
import { useLazyGetAccountLimitsQuery, useLazyGetTransactionalSettingsQuery, useUpdateAccountLimitsMutation, } from '../../services/limitsApi';
import { AccountTransactionalLimitValueStatus, LimitPeriod, LimitTarget, } from '../../types/limits';
import { mapLimitsErrorToMessage } from '../../utils/limitsErrorMapper';
const TRANSACTION_TYPE = 'PIX';
const INITIAL_RULE_SLUGS_MAP = {
    personDay: '',
    personNight: '',
    businessDay: '',
    businessNight: '',
    scheduledAll: '',
};
function formatNumberAsCurrency(value) {
    return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);
}
function mapLimitsToValuesAndSlugs(limits) {
    const getValueAndSlug = (limitPeriod, limitTarget) => {
        const limit = limits.find(l => l.limitPeriod === limitPeriod && l.limitTarget === limitTarget);
        return {
            value: limit ? formatNumberAsCurrency(limit.currentLimitValue) : '0,00',
            slug: limit?.ruleSlug ?? '',
        };
    };
    const personDay = getValueAndSlug(LimitPeriod.DAILY, LimitTarget.PERSON);
    const personNight = getValueAndSlug(LimitPeriod.NIGHTLY, LimitTarget.PERSON);
    const businessDay = getValueAndSlug(LimitPeriod.DAILY, LimitTarget.ORGANIZATION);
    const businessNight = getValueAndSlug(LimitPeriod.NIGHTLY, LimitTarget.ORGANIZATION);
    const scheduledAll = getValueAndSlug(LimitPeriod.ALL, LimitTarget.ALL);
    return {
        values: {
            personDay: personDay.value,
            personNight: personNight.value,
            businessDay: businessDay.value,
            businessNight: businessNight.value,
            scheduledAll: scheduledAll.value,
        },
        slugsMap: {
            personDay: personDay.slug,
            personNight: personNight.slug,
            businessDay: businessDay.slug,
            businessNight: businessNight.slug,
            scheduledAll: scheduledAll.slug,
        },
    };
}
function getPendingLimits(limits) {
    return limits
        .filter(l => l.status === AccountTransactionalLimitValueStatus.PENDING && l.pendingLimitValue !== undefined)
        .map(l => ({
        ruleSlug: l.ruleSlug,
        value: l.pendingLimitValue,
    }));
}
function mapSettingsToMaxLimits(settings) {
    const maxSetting = settings.find(s => s.name === 'SPENDING_LIMIT_MAX_PIX');
    return {
        max: maxSetting?.value ?? 0,
    };
}
export function usePixLimits() {
    const { t } = useTranslation();
    const [getAccountLimits, { data: limitsData, isLoading: isLoadingLimits }] = useLazyGetAccountLimitsQuery();
    const [getTransactionalSettings, { data: settingsData, isLoading: isLoadingSettings }] = useLazyGetTransactionalSettingsQuery();
    const [updateAccountLimits, { isLoading: isSaving }] = useUpdateAccountLimitsMutation();
    const { isFeatureFlagWhiteListed, cache } = useFeatureFlag(undefined, { autoLoad: false });
    const [limits, setLimits] = useState({
        personDay: '0,00',
        personNight: '0,00',
        businessDay: '0,00',
        businessNight: '0,00',
        scheduledAll: '0,00',
    });
    const [originalLimits, setOriginalLimits] = useState(limits);
    const [pendingLimits, setPendingLimits] = useState([]);
    const [ruleSlugsMap, setRuleSlugsMap] = useState(INITIAL_RULE_SLUGS_MAP);
    const [maxLimits, setMaxLimits] = useState({
        max: 0,
    });
    const [accountId, setAccountId] = useState('');
    const [showPinModal, setShowPinModal] = useState(false);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    useEffect(() => {
        const accountInfo = getSelectedAccountInfo();
        if (accountInfo?.accountId) {
            setAccountId(accountInfo.accountId);
            getAccountLimits({
                transactionType: TRANSACTION_TYPE,
                all: false,
            });
            getTransactionalSettings();
        }
    }, [getAccountLimits, getTransactionalSettings]);
    useEffect(() => {
        if (limitsData?.result) {
            const { values, slugsMap } = mapLimitsToValuesAndSlugs(limitsData.result);
            setLimits(values);
            setOriginalLimits(values);
            setRuleSlugsMap(slugsMap);
            setPendingLimits(getPendingLimits(limitsData.result));
        }
    }, [limitsData]);
    useEffect(() => {
        if (settingsData?.result) {
            const maxValues = mapSettingsToMaxLimits(settingsData.result);
            setMaxLimits(maxValues);
        }
    }, [settingsData]);
    const hasChanges = useCallback(() => {
        return (limits.personDay !== originalLimits.personDay ||
            limits.personNight !== originalLimits.personNight ||
            limits.businessDay !== originalLimits.businessDay ||
            limits.businessNight !== originalLimits.businessNight ||
            limits.scheduledAll !== originalLimits.scheduledAll);
    }, [limits, originalLimits]);
    const handleLimitChange = useCallback((field, value) => {
        setLimits(prev => ({ ...prev, [field]: value }));
    }, []);
    const handleCancel = useCallback(() => {
        setLimits(originalLimits);
    }, [originalLimits]);
    const handleOpenPinModal = useCallback(() => {
        if (!hasChanges())
            return;
        setShowPinModal(true);
    }, [hasChanges]);
    const handlePinCancel = useCallback(() => {
        setShowPinModal(false);
    }, []);
    const handleCloseSuccessAlert = useCallback(() => {
        setShowSuccessAlert(false);
    }, []);
    const handleCloseErrorAlert = useCallback(() => {
        setShowErrorAlert(false);
    }, []);
    const handleSave = useCallback(async (credential) => {
        if (!accountId || !hasChanges())
            return;
        const limitsToUpdate = [];
        const addLimitIfChanged = (field) => {
            const ruleSlug = ruleSlugsMap[field];
            if (limits[field] !== originalLimits[field] && ruleSlug) {
                limitsToUpdate.push({
                    accountTransactionalRule: ruleSlug,
                    limitValue: parseCurrencyToNumber(limits[field]),
                });
            }
        };
        addLimitIfChanged('personDay');
        addLimitIfChanged('personNight');
        addLimitIfChanged('businessDay');
        addLimitIfChanged('businessNight');
        addLimitIfChanged('scheduledAll');
        if (!limitsToUpdate?.length)
            return;
        const shouldDelay = isFeatureFlagWhiteListed(FeatureFlagUseCase.CORE_BANKING_UPDATE_PIX_LIMITS_DELAY, undefined, cache?.data);
        try {
            await updateAccountLimits({
                transactionType: TRANSACTION_TYPE,
                body: {
                    limits: limitsToUpdate,
                    delay: shouldDelay,
                    credential,
                },
            }).unwrap();
            setShowPinModal(false);
            setShowSuccessAlert(true);
            getAccountLimits({
                transactionType: TRANSACTION_TYPE,
                all: false,
            });
        }
        catch (error) {
            setShowPinModal(false);
            setErrorMessage(mapLimitsErrorToMessage(error, t));
            setShowErrorAlert(true);
        }
    }, [
        accountId,
        limits,
        originalLimits,
        ruleSlugsMap,
        hasChanges,
        updateAccountLimits,
        getAccountLimits,
        t,
        isFeatureFlagWhiteListed,
        cache,
    ]);
    const handlePinConfirm = useCallback(async (pin) => {
        await handleSave(pin);
    }, [handleSave]);
    return {
        limits,
        originalLimits,
        pendingLimits,
        ruleSlugsMap,
        maxLimits,
        isLoading: isLoadingLimits || isLoadingSettings,
        isSaving,
        hasChanges: hasChanges(),
        showPinModal,
        showSuccessAlert,
        showErrorAlert,
        errorMessage,
        handleLimitChange,
        handleOpenPinModal,
        handlePinConfirm,
        handlePinCancel,
        handleCloseSuccessAlert,
        handleCloseErrorAlert,
        handleCancel,
    };
}
